// src/features/fiscalization/CorrectionReceiptModal.tsx
// Issues a correction receipt against a previously issued receipt — either
// self-initiated (an error was found) or by tax-authority order.
// Covers: CreateCorrectionReceipt.

import { useState } from 'react';
import { useI18n } from '@react/hooks';
import { Button, Input } from '@react/components';
import { Select } from '@/components/Select';
import type { FiscalReceipt, CorrectionType } from '@core/types/fiscalization';
import { CorrectionTypeValues } from '@core/types/fiscalization';
import { errMessage, humanizeEnum, formatMoney } from './format';

const CORRECTION_TYPES = CorrectionTypeValues.filter((v) => v !== 'CORRECTION_TYPE_UNSPECIFIED');

export interface CorrectionReceiptValue {
  correction_type: CorrectionType;
  correction_reason: string;
}

export interface CorrectionReceiptModalProps {
  open: boolean;
  receipt: FiscalReceipt | null;
  isSaving?: boolean;
  onCancel: () => void;
  onSubmit: (value: CorrectionReceiptValue) => Promise<void>;
}

export function CorrectionReceiptModal({ open, receipt, isSaving, onCancel, onSubmit }: CorrectionReceiptModalProps) {
  const { t } = useI18n();
  const [correctionType, setCorrectionType] = useState<CorrectionType>(CORRECTION_TYPES[0]);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (!open || !receipt) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await onSubmit({ correction_type: correctionType, correction_reason: reason });
    } catch (err) {
      setError(errMessage(err, t('fiscalization.receipts.correctionError')));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={onCancel} aria-hidden="true" />
      <form onSubmit={handleSubmit} role="dialog" aria-modal="true" className="relative mx-4 w-full max-w-sm rounded-lg border border-border bg-background p-5 shadow-xl space-y-4">
        <h3 className="text-base font-semibold text-foreground">{t('fiscalization.receipts.correction')}</h3>
        <p className="text-sm text-muted-foreground">{formatMoney(receipt.total_amount)}</p>
        {error && <p className="text-sm text-destructive">{error}</p>}

        <Select label={t('fiscalization.receipts.correctionType')} value={correctionType} onChange={(e) => setCorrectionType(e.target.value as CorrectionType)}>
          {CORRECTION_TYPES.map((v) => (
            <option key={v} value={v}>{humanizeEnum(v, 'CORRECTION_TYPE_')}</option>
          ))}
        </Select>
        <Input label={t('fiscalization.receipts.correctionReason')} value={reason} onChange={(e) => setReason(e.target.value)} required />

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onCancel} disabled={isSaving} className="rounded-md px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted disabled:opacity-50">
            {t('common.cancel')}
          </button>
          <Button type="submit" size="sm" isLoading={isSaving}>{t('fiscalization.receipts.issueCorrection')}</Button>
        </div>
      </form>
    </div>
  );
}

export default CorrectionReceiptModal;
